import { useCallback, useRef, useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

const formatInterval = (totalHours) => {
  if (!totalHours) return 'Immediate'
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  if (days > 0 && hours > 0) return `${days}d ${hours}h wait`
  if (days > 0) return `${days}d wait`
  return `${hours}h wait`
}

const ContentCard = ({
  item,
  index,
  onUpdateContent,
  onRemoveContent,
  dragHandlers,
  isDragging,
  isDragOver,
}) => {
  const [editingInterval, setEditingInterval] = useState(false)
  const [daysValue, setDaysValue] = useState('0')
  const [hoursValue, setHoursValue] = useState('0')

  const startEditingInterval = useCallback(() => {
    const totalHours = item.intervalHours || 0
    setDaysValue(String(Math.floor(totalHours / 24)))
    setHoursValue(String(totalHours % 24))
    setEditingInterval(true)
  }, [item.intervalHours])

  const handleIntervalSave = useCallback(() => {
    const days = parseInt(daysValue, 10) || 0
    const hours = parseInt(hoursValue, 10) || 0
    onUpdateContent(item.id, { intervalHours: days * 24 + hours })
    setEditingInterval(false)
  }, [item.id, daysValue, hoursValue, onUpdateContent])

  const channelColors = {
    email: 'primary',
    sms: 'secondary',
    push: 'info',
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {/* Interval connector */}
      {index > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            my: 0.5,
          }}
        >
          <Box
            sx={{
              width: 2,
              height: 12,
              backgroundColor: 'divider',
            }}
          />
          {editingInterval ? (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 0.5 }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  handleIntervalSave()
                }
              }}
            >
              <MuiTextField
                value={daysValue}
                onChange={(e) => setDaysValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleIntervalSave()
                  if (e.key === 'Escape') setEditingInterval(false)
                }}
                type="number"
                size="small"
                autoFocus
                sx={{ width: 60 }}
                InputProps={{
                  sx: { fontSize: 12, textAlign: 'center' },
                  inputProps: { min: 0 },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                d
              </Typography>
              <MuiTextField
                value={hoursValue}
                onChange={(e) => setHoursValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleIntervalSave()
                  if (e.key === 'Escape') setEditingInterval(false)
                }}
                type="number"
                size="small"
                sx={{ width: 60 }}
                InputProps={{
                  sx: { fontSize: 12, textAlign: 'center' },
                  inputProps: { min: 0, max: 23 },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                h
              </Typography>
            </Box>
          ) : (
            <Chip
              label={formatInterval(item.intervalHours)}
              size="small"
              variant="outlined"
              onClick={startEditingInterval}
              sx={{
                my: 0.5,
                fontSize: 11,
                height: 22,
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
            />
          )}
          <ArrowDownwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Box>
      )}

      {/* Content card */}
      <Box
        draggable
        {...dragHandlers}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          maxWidth: 500,
          border: 2,
          borderColor: isDragOver
            ? 'primary.main'
            : isDragging
            ? 'primary.light'
            : 'divider',
          borderRadius: 2,
          borderStyle: isDragOver ? 'dashed' : 'solid',
          backgroundColor: isDragging ? 'action.hover' : 'background.paper',
          opacity: isDragging ? 0.6 : 1,
          px: 2,
          py: 1.5,
          transition: 'border-color 0.15s, opacity 0.15s',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon
          sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            {index + 1}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {item.template?.name || item.templateId}
          </Typography>
          {item.template?.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {item.template.description}
            </Typography>
          )}
        </Box>

        <Chip
          label={item.template?.channel || '?'}
          size="small"
          color={channelColors[item.template?.channel] || 'default'}
          sx={{ flexShrink: 0 }}
        />

        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            onRemoveContent(item.id)
          }}
          sx={{ flexShrink: 0 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
}

const ContentWorkflow = ({
  content,
  onUpdateContent,
  onRemoveContent,
  onReorder,
}) => {
  const [dragIndex, setDragIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const dragCounter = useRef({})

  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
  }, [])

  const handleDragEnter = useCallback((e, index) => {
    e.preventDefault()
    dragCounter.current[index] = (dragCounter.current[index] || 0) + 1
    setDragOverIndex(index)
  }, [])

  const handleDragLeave = useCallback((e, index) => {
    dragCounter.current[index] = (dragCounter.current[index] || 0) - 1
    if (dragCounter.current[index] <= 0) {
      dragCounter.current[index] = 0
      setDragOverIndex((prev) => (prev === index ? null : prev))
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault()
      dragCounter.current = {}
      const fromIndex = dragIndex
      if (fromIndex === null || fromIndex === dropIndex) {
        setDragIndex(null)
        setDragOverIndex(null)
        return
      }

      const reordered = [...content]
      const [moved] = reordered.splice(fromIndex, 1)
      reordered.splice(dropIndex, 0, moved)
      onReorder(reordered)

      setDragIndex(null)
      setDragOverIndex(null)
    },
    [content, dragIndex, onReorder],
  )

  const handleDragEnd = useCallback(() => {
    setDragIndex(null)
    setDragOverIndex(null)
    dragCounter.current = {}
  }, [])

  if (content.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          borderStyle: 'dashed',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No content steps yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click "Add Step" to build your campaign workflow
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
      }}
    >
      {content.map((item, index) => (
        <ContentCard
          key={item.id}
          item={item}
          index={index}
          onUpdateContent={onUpdateContent}
          onRemoveContent={onRemoveContent}
          isDragging={dragIndex === index}
          isDragOver={dragOverIndex === index && dragIndex !== index}
          dragHandlers={{
            onDragStart: (e) => handleDragStart(e, index),
            onDragEnter: (e) => handleDragEnter(e, index),
            onDragLeave: (e) => handleDragLeave(e, index),
            onDragOver: handleDragOver,
            onDrop: (e) => handleDrop(e, index),
            onDragEnd: handleDragEnd,
          }}
        />
      ))}
    </Box>
  )
}

export default ContentWorkflow
